import { useState, useMemo, useRef, useCallback } from "react";
import { Box, Button, Chip, Typography, keyframes } from "@mui/material";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import useTitle from "components/hooks/useTitle";
import useIsMobile from "components/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import inspirationItems from "../data/inspirationItems.json";
import {
  FEATURE_FLAGS,
  isFeatureEnabled,
} from "components/helpers/GatedComponent";
import {
  Calendar,
  Home,
  User,
  Zap,
  Bell,
  Sparkles,
  Mail,
  type LucideIcon,
} from "lucide-react";

type InspirationItem = {
  title: string;
  description: string;
  category: string;
  conversationStarter?: string | null;
};

const items = inspirationItems as InspirationItem[];

const mailItems: InspirationItem[] = [
  {
    title: "Build a household dossier",
    description:
      "Have your agent comb through your Gmail history to assemble a reference dossier any helper can start from on day one.",
    category: "Mail",
    conversationStarter:
      "Dig deep through my Gmail history and build a dossier of my household that a new personal assistant could start from. Include the key people in my life, our home address, important contacts, schedules, and the kind of details a nanny, handyman, or house manager would need to work from.",
  },
  {
    title: "Summarize my unread inbox",
    description:
      "Get a digest of your unread messages with suggestions for what to clear out (heads up: I can't mark things as read for you).",
    category: "Mail",
    conversationStarter:
      "Summarize my unread emails and suggest what I can safely clean up. Note that you can't mark messages as read, so just point me to what deserves a reply and what I can ignore.",
  },
  {
    title: "Find what to unsubscribe from",
    description:
      "See which senders are flooding your inbox and get a ranked list of newsletters and alerts worth unsubscribing from.",
    category: "Mail",
    conversationStarter:
      "Look at which companies and senders are filling up my inbox and suggest the ones I should unsubscribe from. Give me a ranked list of the worst offenders with how often each one emails me.",
  },
  {
    title: "Catch returns before the window closes",
    description:
      "Surface recent orders and receipts that are likely still within their return window so you can make keep-or-return calls in time.",
    category: "Mail",
    conversationStarter:
      "Go through my recent orders and receipts and find purchases that are likely still within their return window. Flag anything I should make a keep-or-return decision on before it's too late.",
  },
  {
    title: "Round up expensable purchases",
    description:
      "Review the last 60 days of purchases and flag anything you might need to submit to work, insurance, or an FSA/HSA.",
    category: "Mail",
    conversationStarter:
      "Look through my purchases from the last 60 days and flag anything I might need to expense — work reimbursements, insurance claims, FSA/HSA, etc. Organize them so I can act on each one.",
  },
];

const ALL_FILTER = "All";

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const categoryIcons: Record<string, LucideIcon> = {
  All: Sparkles,
  Mail: Mail,
  Calendar: Calendar,
  Home: Home,
  Personal: User,
  Productivity: Zap,
  Reminders: Bell,
};

const InspirationCard = ({
  item,
  onTryIt,
}: {
  item: InspirationItem;
  onTryIt?: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = categoryIcons[item.category];

  return (
    <Box
      sx={{
        border: "1px solid #D4D4D4",
        borderRadius: "12px",
        padding: "16px 20px",
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
        cursor: "pointer",
      }}
      onClick={() => setExpanded((prev) => !prev)}
    >
      {Icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "10px",
            backgroundColor: "#EDF8F6",
            flexShrink: 0,
            marginTop: "2px",
          }}
        >
          <Icon size={20} strokeWidth={1.5} color="#198282" />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body" fontWeight={600}>
          {item.title}
        </Typography>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: expanded ? "unset" : 1,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {item.description}
        </Typography>
      </Box>
      {onTryIt && (
        <Button
          variant="outlined"
          size="small"
          sx={{
            flexShrink: 0,
            height: "32px",
            marginTop: "4px",
            alignSelf: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onTryIt();
          }}
        >
          Try it
        </Button>
      )}
    </Box>
  );
};

export default () => {
  useTitle("Inspiration");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(ALL_FILTER);
  const animationKey = useRef(0);

  const handleFilterChange = useCallback((cat: string) => {
    animationKey.current += 1;
    setSelectedCategory(cat);
  }, []);

  const allItems = useMemo(() => {
    const gmailEnabled = isFeatureEnabled(
      FEATURE_FLAGS.GMAIL_INTEGRATION.value,
      true,
    );
    return gmailEnabled ? [...items, ...mailItems] : items;
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(allItems.map((item) => item.category)));
    unique.sort();
    return [ALL_FILTER, ...unique];
  }, [allItems]);

  const filtered =
    selectedCategory === ALL_FILTER
      ? allItems
      : allItems.filter((item) => item.category === selectedCategory);

  const handleTryIt = (item: InspirationItem) => {
    navigate("/conversations/in-app", {
      state: { prefill: item.conversationStarter },
    });
  };

  return (
    <FlexPage
      leftAligned
      sx={{ padding: isMobile ? "32px 24px" : "64px 32px" }}
    >
      <Breadcrumbs
        breadcrumbs={[{ name: "Home", link: "/home" }, { name: "Inspiration" }]}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: isMobile ? "20px" : "32px",
        }}
      >
        <Typography variant="display">Inspiration</Typography>
        <Typography variant="body" sx={{ maxWidth: "550px" }}>
          Explore ways your agent can help you stay organized and on top of
          everything.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "20px" : "32px",
          width: "100%",
        }}
      >
        {/* Sidebar filters */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "8px" : "4px",
            flexShrink: 0,
            minWidth: isMobile ? undefined : "170px",
            justifyContent: isMobile ? "center" : undefined,
          }}
        >
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat;
            const Icon = categoryIcons[cat];
            return isMobile ? (
              <Chip
                key={cat}
                label={cat}
                onClick={() => handleFilterChange(cat)}
                variant={isSelected ? "filled" : "outlined"}
                color={isSelected ? "primary" : "default"}
                sx={{ fontWeight: isSelected ? 600 : 400, cursor: "pointer" }}
              />
            ) : (
              <Box
                key={cat}
                onClick={() => handleFilterChange(cat)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#EDF8F6" : "transparent",
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: isSelected ? "#E0F3F1" : "action.hover",
                  },
                }}
              >
                {Icon && (
                  <Icon
                    size={20}
                    strokeWidth={2}
                    color={isSelected ? "#198282" : "#1D2025"}
                  />
                )}
                <Typography
                  variant="body"
                  fontWeight={isSelected ? 700 : 500}
                  color={isSelected ? "#198282" : "#1D2025"}
                  sx={{ whiteSpace: "nowrap", fontSize: "15px" }}
                >
                  {cat}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Cards list */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {filtered.map((item, index) => (
            <Box
              key={`${animationKey.current}-${item.title}`}
              sx={{
                animation: `${fadeSlideIn} 0.7s cubic-bezier(0.2, 0, 0, 1) both`,
                animationDelay: `${index * 100}ms`,
              }}
            >
              <InspirationCard
                item={item}
                onTryIt={
                  item.conversationStarter ? () => handleTryIt(item) : undefined
                }
              />
            </Box>
          ))}
        </Box>
      </Box>
    </FlexPage>
  );
};
